import { forwardRef } from "react";

import {
  ConversationMessage,
  ConversationMessageRoleEnum,
  ConverseResponse,
  ToolUse,
  ToolUseTypeEnum,
} from "src/clients/copilot";
import { ConversationMessageItem } from "src/copilot/ConversationMessageItem";
import { LoadingMessageItem } from "src/copilot/LoadingMessageItem";
import { NodeSuggestionType } from "src/copilot/NodeSuggestion";
import { QuerySuggestionType } from "src/copilot/QuerySuggestion";
import { ToolApprovalResultCard } from "src/copilot/ToolApprovalResultCard";
import {
  FeatureQueryResource,
  NodeResource,
  ResourceType,
} from "src/copilot/utils";

const getToolApprovalOrResult = (
  message: ConversationMessage,
): ToolUse | null => {
  if (message.role !== ConversationMessageRoleEnum.USER) return null;
  if (!message.tool_use) return null;

  if (
    message.tool_use.type === ToolUseTypeEnum.APPROVAL ||
    message.tool_use.type === ToolUseTypeEnum.RESULT
  ) {
    return message.tool_use;
  }

  return null;
};

type Props = {
  resourceData: NodeResource | FeatureQueryResource;
  resourceType: ResourceType;
  conversation: ConverseResponse;
  loading?: boolean;
  retrying?: boolean;
  regenerating?: boolean;
  indexToAnimate: number;
  onApply: (suggestion: NodeSuggestionType | QuerySuggestionType) => void;
  onRegenerate: VoidFunction;
  onToolApproval: (toolUse: ToolUse, approved: boolean) => void;
  canEditFlowVersion: boolean;
};

export const ConversationMessageList = forwardRef<HTMLUListElement, Props>(
  (
    {
      conversation,
      loading,
      retrying,
      regenerating,
      resourceData,
      resourceType,
      onApply,
      onRegenerate,
      onToolApproval,
      indexToAnimate,
      canEditFlowVersion,
    },
    ref,
  ) => {
    const loadingMode = (() => {
      switch (true) {
        case regenerating:
          return "regenerating";
        case retrying:
          return "retrying";
        default:
          return "loading";
      }
    })();

    return (
      <ul
        ref={ref}
        className="decideScrollbar flex h-full flex-col gap-y-4 px-2 py-2"
      >
        {conversation.messages.map((message, index) => {
          const toolApprovalOrResult = getToolApprovalOrResult(message);
          if (toolApprovalOrResult) {
            return (
              <li key={`${message.role}-${index}`}>
                <ToolApprovalResultCard toolUse={toolApprovalOrResult} />
              </li>
            );
          }

          const isLastMessage = index === conversation.messages.length - 1;

          return (
            <ConversationMessageItem
              key={`${message.role}-${index}`}
              animate={indexToAnimate === index}
              canEditFlowVersion={canEditFlowVersion}
              completionId={isLastMessage ? conversation.completion_id : null}
              conversationId={conversation.conversation_id}
              isLastMessage={isLastMessage}
              isLoading={loading}
              message={message}
              resourceData={resourceData}
              resourceType={resourceType}
              onApply={onApply}
              onRegenerate={onRegenerate}
              onToolApproval={onToolApproval}
            />
          );
        })}
        {loading && <LoadingMessageItem mode={loadingMode} />}
      </ul>
    );
  },
);
